import { type LazyExoticComponent, lazy } from 'react';

import { reloadPage } from '@/utils/window-utils.ts';

export const CHUNK_RELOAD_STORAGE_KEY = 'chunk-reload-at';
export const CHUNK_RELOAD_GUARD_MS = 5 * 60 * 1000;
export const UNLOAD_TIMEOUT_MS = 10 * 1000;

let isReloading = false;

const parseReloadMarker = (marker: string | undefined, now: number): number | undefined => {
    if (!marker) return undefined;
    const timestamp = Number(marker);
    return Number.isFinite(timestamp) && timestamp <= now ? timestamp : undefined;
};

const tryMarkReload = (now: number): boolean => {
    try {
        const lastReloadAt = parseReloadMarker(sessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY) ?? undefined, now);
        if (lastReloadAt !== undefined && now - lastReloadAt < CHUNK_RELOAD_GUARD_MS) return false;

        sessionStorage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(now));
        return true;
    } catch {
        return false;
    }
};

const clearReloadMarker = (): void => {
    try {
        sessionStorage.removeItem(CHUNK_RELOAD_STORAGE_KEY);
    } catch {
        // intentionally empty: the page already loaded
    }
};

const waitForUnload = async (error: unknown): Promise<never> => {
    await new Promise((resolve) => setTimeout(resolve, UNLOAD_TIMEOUT_MS));
    isReloading = false;
    throw error;
};

// After a deploy an open tab still references chunk files that no longer exist; a reload fetches the new ones.
export const handleChunkLoadError = async (error: unknown): Promise<never> => {
    if (!isReloading) {
        if (!tryMarkReload(Date.now())) throw error;

        isReloading = true;
        reloadPage();
    }
    return await waitForUnload(error);
};

type LazyComponent = Awaited<ReturnType<Parameters<typeof lazy>[0]>>['default'];

export const lazyWithReload = <T extends LazyComponent>(
    factory: () => Promise<{ default: T }>
): LazyExoticComponent<T> =>
    lazy(async () => {
        let module: { default: T };
        try {
            module = await factory();
        } catch (error) {
            return await handleChunkLoadError(error);
        }
        if (!isReloading) clearReloadMarker();
        return module;
    });
